import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Camera, Video } from "lucide-react";

export default function CandidateStart() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ candidate_name: "", candidate_email: "" });

  useEffect(() => {
    api
      .get(`/jobs/${jobId}/public`)
      .then(({ data }) => setJob(data))
      .catch(() => toast.error("Interview link is invalid"))
      .finally(() => setLoading(false));
  }, [jobId]);

  const start = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/interviews", {
        job_id: jobId,
        ...form,
      });
      navigate(`/interview/session/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start interview");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-900 text-white">
        Loading…
      </main>
    );
  }

  if (!job) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-900 text-white">
        Invalid interview link.
      </main>
    );
  }

  return (
    <main
      data-testid="candidate-start-page"
      className="grain-overlay min-h-screen bg-slate-900 text-white"
    >
      <div className="mx-auto grid min-h-screen max-w-6xl gap-12 px-6 py-16 lg:grid-cols-2 lg:items-center">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-300">
            <Video className="h-3 w-3" /> Hireloop PRO interview
          </div>
          <h1 className="mt-4 font-heading text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            You've been invited to interview for
            <br />
            <span className="text-blue-300">{job.title}</span>
          </h1>
          {job.description && (
            <p className="mt-6 max-w-lg text-base text-slate-300">{job.description}</p>
          )}
          <div className="mt-10 space-y-3 text-sm text-slate-300">
            <div className="flex items-center gap-3">
              <span className="font-mono text-blue-300">01</span>
              Enter your name and email.
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-blue-300">02</span>
              We'll show you {job.questions?.length || "a few"} questions.
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-blue-300">03</span>
              Record a short video answer for each one.
            </div>
          </div>
        </div>

        <form
          onSubmit={start}
          className="border border-white/10 bg-white/5 p-8 backdrop-blur-xl"
        >
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-300">
            <Camera className="h-3 w-3" /> Get ready
          </div>
          <h2 className="mt-3 font-heading text-2xl font-medium">
            Tell us who you are
          </h2>

          <div className="mt-6 space-y-5">
            <div>
              <Label
                htmlFor="candidate-name"
                className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400"
              >
                Full name
              </Label>
              <Input
                id="candidate-name"
                data-testid="candidate-name-input"
                required
                value={form.candidate_name}
                onChange={(e) =>
                  setForm({ ...form, candidate_name: e.target.value })
                }
                className="mt-2 rounded-sm border-white/20 bg-white/5 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label
                htmlFor="candidate-email"
                className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400"
              >
                Email
              </Label>
              <Input
                id="candidate-email"
                data-testid="candidate-email-input"
                type="email"
                required
                value={form.candidate_email}
                onChange={(e) =>
                  setForm({ ...form, candidate_email: e.target.value })
                }
                className="mt-2 rounded-sm border-white/20 bg-white/5 text-white placeholder:text-slate-500"
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="candidate-start-btn"
            disabled={submitting}
            className="mt-8 w-full rounded-sm bg-blue-600 py-6 text-base hover:bg-blue-500"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Start interview
          </Button>
          <p className="mt-4 text-xs text-slate-500">
            By continuing you agree that your camera and microphone will be used
            to record your answers.
          </p>
        </form>
      </div>
    </main>
  );
}
